<script setup lang="ts">
/**
 * A moiré pattern (utils/moire.ts) filling its positioned parent.
 *
 * Colors come from CSS, so each host can theme it, and they are re-read when
 * the theme changes:
 *   --moire-ground  behind the lines (default: transparent)
 *   --moire-line    the lines (default: the muted text color, faint)
 *   --moire-accent  points, hubs and the second dot screen (default: the accent)
 */
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { mountMoire, type MoireController, type MoireOptions, type MoireParams, type MoirePattern } from '../utils/moire'

const props = withDefaults(defineProps<{
  pattern: MoirePattern
  params?: MoireParams
  /** Ease parameter changes over this many ms. */
  ease?: number
  loop?: boolean
  step?: number
  still?: number
  origin?: MoireOptions['origin']
  clear?: HTMLElement | null
  clearGap?: number
  clearRadius?: number
  clearFade?: number
  mark?: Element | null
  /** Re-read the colors whenever this changes (a state class on an ancestor, say). */
  recolor?: unknown
}>(), {
  params: () => ({}),
  ease: 0,
  loop: false,
  clear: null,
  mark: null,
})

const host = ref<HTMLElement | null>(null)
const canvas = ref<HTMLCanvasElement | null>(null)
const groundProbe = ref<HTMLElement | null>(null)
const lineProbe = ref<HTMLElement | null>(null)
const accentProbe = ref<HTMLElement | null>(null)

// A context that has been released can't be reused, so each mount gets a fresh canvas.
const canvasKey = ref(0)
let ctrl: MoireController | null = null
let themeWatch: MutationObserver | null = null

const colors = () => ({
  ground: groundProbe.value ? getComputedStyle(groundProbe.value).color : 'transparent',
  line: lineProbe.value ? getComputedStyle(lineProbe.value).color : '#1f7a72',
  accent: accentProbe.value ? getComputedStyle(accentProbe.value).color : '#ff6b45',
})

async function mount() {
  if (ctrl) {
    ctrl.dispose()
    ctrl = null
    canvasKey.value += 1
    await nextTick()
  }
  if (!host.value || !canvas.value) return
  ctrl = mountMoire({
    host: host.value,
    canvas: canvas.value,
    pattern: props.pattern,
    colors: colors(),
    params: { ...props.params },
    loop: props.loop,
    step: props.step,
    still: props.still,
    origin: props.origin,
    clear: props.clear,
    clearGap: props.clearGap,
    clearRadius: props.clearRadius,
    clearFade: props.clearFade,
    mark: props.mark,
  })
}

onMounted(async () => {
  // The parent's refs (mark, clear box) are set once the whole tree has rendered.
  await nextTick()
  mount()
  themeWatch = new MutationObserver(() => ctrl?.setColors(colors()))
  themeWatch.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class', 'data-theme'] })
})

watch(() => props.params, (next) => ctrl?.set({ ...next }, props.ease), { deep: true })
watch(() => props.loop, (next) => ctrl?.setLoop(next))
watch(() => props.recolor, () => nextTick(() => ctrl?.setColors(colors())))
watch(() => [props.pattern, props.mark, props.clear, props.step], mount)

onBeforeUnmount(() => {
  themeWatch?.disconnect()
  ctrl?.dispose()
})

defineExpose({ refresh: () => ctrl?.refresh() })
</script>

<template>
  <span ref="host" class="moire" aria-hidden="true">
    <canvas :key="canvasKey" ref="canvas" class="moire-canvas" />
    <span ref="groundProbe" class="moire-probe moire-probe--ground" />
    <span ref="lineProbe" class="moire-probe moire-probe--line" />
    <span ref="accentProbe" class="moire-probe moire-probe--accent" />
  </span>
</template>

<style scoped>
.moire {
  position: absolute;
  inset: 0;
  display: block;
  overflow: hidden;
  pointer-events: none;
}

.moire-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.moire-probe { display: none; }
.moire-probe--ground { color: var(--moire-ground, transparent); }
.moire-probe--line { color: var(--moire-line, color-mix(in srgb, var(--text-muted, #6b7c78) 30%, transparent)); }
.moire-probe--accent { color: var(--moire-accent, var(--accent, #ff6b45)); }
</style>
